import { apiClient } from '@/services/api';
import type { RequestPinResponse, SessionEnvelope, User } from '@/types/user';

export type AuthChannel = 'sms' | 'email';
export type VerifyPinOptions = {
  refId?: string;
  referralLinkToken?: string;
};

export const authService = {
  requestPin(channel: AuthChannel, contact: string): Promise<RequestPinResponse> {
    return apiClient.post<RequestPinResponse>(
      '/api/v1/auth/request_pin',
      { channel, contact },
      { skipAuth: true },
    );
  },

  /**
   * Escape hatch for a phone that can't receive SMS — a carrier block, a
   * landline, an old STOP. The code goes to the email on that account, but
   * still verifies the PHONE, so `verifyPin` is called with the phone as
   * before and resolves the account they already have.
   *
   * Always resolves. The server answers `{ ok: true }` for every input on
   * purpose (it must not reveal which numbers have accounts), so there is no
   * success/failure here to branch on — the UI can only say "check your email".
   */
  requestPinByEmail(contact: string): Promise<{ ok: true }> {
    return apiClient.post<{ ok: true }>(
      '/api/v1/auth/request_pin_by_email',
      { contact },
      { skipAuth: true },
    );
  },

  verifyPin(contact: string, code: string, options?: VerifyPinOptions): Promise<SessionEnvelope> {
    return apiClient.post<SessionEnvelope>(
      '/api/v1/auth/verify_pin',
      {
        contact,
        code,
        ...(options?.refId ? { ref_id: options.refId } : {}),
        ...(options?.referralLinkToken
          ? { referral_link_token: options.referralLinkToken }
          : {}),
      },
      { skipAuth: true },
    );
  },

  async me(): Promise<User> {
    const { user } = await apiClient.get<{ user: User }>('/api/v1/auth/me');
    return user;
  },

  logout(): Promise<void> {
    return apiClient.post<void>('/api/v1/auth/logout');
  },
};
